import { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { UnitDTO } from "shared/types/unit";

interface RentalStats {
  totalRent: number;
  residential: {
    totalRent: number;
    averageActualRent: number;
    averageActualRentPerSqm: number;
    count: number;
    totalSurface: number;
    ranges: {
      range0_50: RangeData;
      range51_75: RangeData;
      range76_100: RangeData;
      range100plus: RangeData;
    };
  };
  retail: {
    totalRent: number;
    averageActualRent: number;
    averageActualRentPerSqm: number;
    count: number;
    totalSurface: number;
  };
  byRegion: any[];
  occupancyRateByUnits: number;
  occupancyRateByERV: number;
  valueCreation: {
    totalERV: number;
    totalActualRent: number;
    percentageDifference: number;
    unitComparisons: any[];
  };
  occupancyDetails: {
    activeUnits: number;
    totalUnits: number;
    totalERV: number;
    totalActiveContractualRentRent: number;
    unitsWithoutContract: UnitDTO[];
    unitsWithoutERV: UnitDTO[];
  };
}

interface RangeData {
  averageActualRent: number;
  averageActualRentPerSqm: number;
  count: number;
  totalSurface: number;
  occupancyShare: number;
  units: any[];
}

export const useRentalStats = () => {
  const { session } = useSession();
  const [stats, setStats] = useState<RentalStats>({
    totalRent: 0,
    residential: {
      totalRent: 0,
      averageActualRent: 0,
      averageActualRentPerSqm: 0,
      count: 0,
      totalSurface: 0,
      ranges: {
        range0_50: {
          averageActualRent: 0,
          averageActualRentPerSqm: 0,
          count: 0,
          totalSurface: 0,
          occupancyShare: 0,
          units: []
        },
        range51_75: {
          averageActualRent: 0,
          averageActualRentPerSqm: 0,
          count: 0,
          totalSurface: 0,
          occupancyShare: 0,
          units: []
        },
        range76_100: {
          averageActualRent: 0,
          averageActualRentPerSqm: 0,
          count: 0,
          totalSurface: 0,
          occupancyShare: 0,
          units: []
        },
        range100plus: {
          averageActualRent: 0,
          averageActualRentPerSqm: 0,
          count: 0,
          totalSurface: 0,
          occupancyShare: 0,
          units: []
        }
      }
    },
    retail: {
      totalRent: 0,
      averageActualRent: 0,
      averageActualRentPerSqm: 0,
      count: 0,
      totalSurface: 0
    },
    byRegion: [],
    occupancyRateByUnits: 0,
    occupancyRateByERV: 0,
    valueCreation: {
      totalERV: 0,
      totalActualRent: 0,
      percentageDifference: 0,
      unitComparisons: []
    },
    occupancyDetails: {
      activeUnits: 0,
      totalUnits: 0,
      totalERV: 0,
      totalActiveContractualRentRent: 0,
      unitsWithoutContract: [],
      unitsWithoutERV: []
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/rental/metrics/rental-statistics`,
        { method: "GET" },
        session
      );
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch rental statistics:", error);
      setError("Failed to load rental statistics");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      fetchStats();
    }
  }, [session]);

  return { stats, isLoading, error, refetch: fetchStats };
};
